import { isNil } from 'util/null'

type O = object & {
  [Symbol.iterator]?: never;
}

type K<T extends O> = keyof T

export function isObject(value: unknown): value is O {
  return typeof value === 'object' && !Array.isArray(value) && value !== null
}

export function keys<T extends O>(value: T): K<T>[] {
  return Object.keys(value) as K<T>[]
}

type Predicate = (value: unknown, key: string) => boolean

export function omit<T extends O>(value: T, predicate?: Predicate): T {
  const skip = predicate ?? isNil
  return Object.fromEntries(Object.entries(value).filter(([k, v]) => !skip(v, k))) as T
}

export function omitDeep<T extends O>(value: T, predicate?: Predicate): T {
  if (!value) return value
  if (Array.isArray(value)) return value
  if (typeof value != 'object') return value

  const skip = predicate ?? isNil
  const entries = Object.entries(value)
    .filter(([k, v]) => !skip(v, k))
    .map(([k, v]) => [k, isObject(v) ? omitDeep(v) : v])
  return Object.fromEntries(entries) as T
}

export function isEmptyObject(value: object): boolean {
  return Object.entries(value).length === 0
}

export function emptyObjectToUndefined<T extends O>(value: T): T | undefined {
  return isEmptyObject(value) ? undefined : value
}

export function getKeys<T>(value: T | null | undefined): (keyof T)[] {
  if (!isObject(value)) return []
  return Object.keys(value) as (keyof T)[]
}

function updateObjectUnsafe(original: any, update: any): any {
  original = isObject(original) ? original : {}
  update = isObject(update) ? update : {}
  const copy = { ...original, ...update }
  for (const key of getKeys(copy)) {
    const prev = original[key]
    const next = update[key]
    if (!Object.hasOwn(update, key)) {
      copy[key] = prev
    } else if (next === undefined) {
      delete copy[key]
    } else if (next === null || Array.isArray(next)) {
      copy[key] = next
    } else if (isObject(next)) {
      copy[key] = updateObjectUnsafe(prev, next)
    } else {
      copy[key] = next
    }
  }
  return copy
}

export function updateObject<T extends object>(original: T, update: Partial<T>): T {
  return updateObjectUnsafe(original, update)
}

export function toUndefined<T>(value: T | null | undefined): T | undefined {
  if (value == null) return undefined
  if (value instanceof Array && !value.length) return undefined
  if (value instanceof Object && !Object.keys(value).length) return undefined
  return value
}

/** simplify an object in place */
export function simplify(object: any, keys: string[]) {
  if (!object || typeof object !== 'object' || Array.isArray(object)) return

  for (const key of Object.keys(object)) {
    const keep = keys.includes(key)
    if (!keep) delete object[key]
  }
}
